import { useState, useEffect, useCallback, useRef } from "react";
import type {
  TaxReturn,
  PendingUpload,
  FileProgress,
  FileWithId,
} from "./lib/schema";
import type { NavItem } from "./lib/types";
import {
  type ModelConfig,
  type ProviderId,
  type ProviderKeyStatus,
  DEFAULT_MODEL_CONFIG,
  EMPTY_PROVIDER_KEY_STATUS,
  PROVIDER_LABELS,
  getProviderForFamily,
} from "./lib/model-catalog";
import { MainPanel } from "./components/MainPanel";
import { UploadModal } from "./components/UploadModal";
import { SettingsModal } from "./components/SettingsModal";
import { ResetDialog } from "./components/ResetDialog";
import { SetupDialog } from "./components/SetupDialog";
import { Chat, type ChatMessage } from "./components/Chat";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { DevTools } from "./components/DevTools";
import { HelpDialog } from "./components/HelpDialog";
import { extractYearFromFilename } from "./lib/year-extractor";
import "./index.css";

async function withConcurrency<T>(
  items: T[],
  limit: number,
  fn: (item: T) => Promise<void>,
) {
  const queue = [...items];
  const workers = Array.from(
    { length: Math.min(limit, items.length) },
    async () => {
      while (queue.length > 0) {
        const item = queue.shift()!;
        await fn(item);
      }
    },
  );
  await Promise.all(workers);
}

const CHAT_OPEN_KEY = "tax-chat-open";
const CHAT_HISTORY_KEY = "tax-chat-history";
const THEME_KEY = "tax-theme";

function loadChatMessages(): ChatMessage[] {
  try {
    const stored = localStorage.getItem(CHAT_HISTORY_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return [];
}

function saveChatMessages(messages: ChatMessage[]) {
  try {
    localStorage.setItem(CHAT_HISTORY_KEY, JSON.stringify(messages));
  } catch {}
}

type SelectedView = "summary" | number | `pending:${string}`;

interface AppState {
  returns: Record<number, TaxReturn>;
  providerKeys: ProviderKeyStatus;
  selectedYear: SelectedView;
  isLoading: boolean;
  hasUserData: boolean;
  isDev: boolean;
  modelConfig: ModelConfig;
}

async function fetchInitialState(): Promise<
  Pick<
    AppState,
    "returns" | "providerKeys" | "hasUserData" | "isDev" | "modelConfig"
  >
> {
  const [configRes, returnsRes] = await Promise.all([
    fetch("/api/config"),
    fetch("/api/returns"),
  ]);
  const { hasKey, hasKeys, isDev, modelConfig } = await configRes.json();
  const returns = await returnsRes.json();
  const hasUserData = Object.keys(returns).length > 0;
  return {
    providerKeys: hasKeys ?? {
      ...EMPTY_PROVIDER_KEY_STATUS,
      anthropic: Boolean(hasKey),
    },
    returns,
    hasUserData,
    isDev: isDev ?? false,
    modelConfig: modelConfig ?? DEFAULT_MODEL_CONFIG,
  };
}

function getDefaultSelection(returns: Record<number, TaxReturn>): SelectedView {
  const years = Object.keys(returns)
    .map(Number)
    .sort((a, b) => a - b);
  if (years.length === 0) return "summary";
  if (years.length === 1) return years[0] ?? "summary";
  return "summary";
}

function buildNavItems(returns: Record<number, TaxReturn>): NavItem[] {
  const years = Object.keys(returns)
    .map(Number)
    .sort((a, b) => b - a);
  const items: NavItem[] = [];
  if (years.length > 1) items.push({ id: "summary", label: "All time" });
  items.push(...years.map((y) => ({ id: String(y), label: String(y) })));
  return items;
}

function parseSelectedId(id: string): SelectedView {
  if (id === "summary") return "summary";
  if (id.startsWith("pending:")) return id as `pending:${string}`;
  return Number(id);
}

export function App() {
  const [state, setState] = useState<AppState>({
    returns: {},
    providerKeys: { ...EMPTY_PROVIDER_KEY_STATUS },
    selectedYear: "summary",
    isLoading: true,
    hasUserData: false,
    isDev: false,
    modelConfig: { ...DEFAULT_MODEL_CONFIG },
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [configureKeyOnly, setConfigureKeyOnly] = useState(false);
  const [pendingUploads, setPendingUploads] = useState<PendingUpload[]>([]);
  const [isChatOpen, setIsChatOpen] = useState(() => {
    const stored = localStorage.getItem(CHAT_OPEN_KEY);
    if (stored !== null) {
      return stored === "true";
    }
    // Default: closed on mobile, open on desktop
    return typeof window !== "undefined" && window.innerWidth >= 768;
  });
  const [openModal, setOpenModal] = useState<
    "settings" | "reset" | "onboarding" | "help" | null
  >(null);
  const [onboardingDismissed, setOnboardingDismissed] = useState(false);
  const [isOnboardingProcessing, setIsOnboardingProcessing] = useState(false);
  const [onboardingProgress, setOnboardingProgress] = useState<FileProgress[]>(
    [],
  );
  const [isDark, setIsDark] = useState(() => {
    if (typeof window === "undefined") return false;
    const stored = localStorage.getItem(THEME_KEY);
    if (stored !== null) return stored === "dark";
    return false; // Default to light mode
  });
  const [devTriggerError, setDevTriggerError] = useState(false);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>(() =>
    loadChatMessages(),
  );
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [pendingAutoMessage, setPendingAutoMessage] = useState<string | null>(
    null,
  );
  const [followUpSuggestions, setFollowUpSuggestions] = useState<string[]>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const hasShownOnboardingRef = useRef(false);

  const parseProvider = getProviderForFamily(state.modelConfig.parseFamily);
  const parseProviderLabel = PROVIDER_LABELS[parseProvider];
  const hasParseProviderKey = state.providerKeys[parseProvider];
  const answerProvider = getProviderForFamily(state.modelConfig.answerFamily);
  const hasAnswerProviderKey = state.providerKeys[answerProvider];
  const hasAnyProviderKey = Object.values(state.providerKeys).some(Boolean);

  const effectiveReturns = state.returns;
  const navItems = buildNavItems(state.returns);

  useEffect(() => {
    fetchInitialState()
      .then(({ returns, providerKeys, hasUserData, isDev, modelConfig }) => {
        setState({
          returns,
          providerKeys,
          selectedYear: getDefaultSelection(returns),
          isLoading: false,
          hasUserData,
          isDev,
          modelConfig,
        });
      })
      .catch((err) => {
        console.error("Failed to load:", err);
        setState((s) => ({ ...s, isLoading: false }));
      });
  }, []);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", isDark);
    localStorage.setItem(THEME_KEY, isDark ? "dark" : "light");
  }, [isDark]);

  useEffect(() => {
    localStorage.setItem(CHAT_OPEN_KEY, String(isChatOpen));
  }, [isChatOpen]);

  useEffect(() => {
    saveChatMessages(chatMessages);
  }, [chatMessages]);

  // Auto-submit pending message when chat is ready
  useEffect(() => {
    if (pendingAutoMessage && isChatOpen && !isChatLoading) {
      submitChatMessage(pendingAutoMessage);
      setPendingAutoMessage(null);
    }
  }, [pendingAutoMessage, isChatOpen, isChatLoading]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLInputElement ||
        e.target instanceof HTMLTextAreaElement
      ) {
        return;
      }

      const currentId =
        state.selectedYear === "summary"
          ? "summary"
          : String(state.selectedYear);
      const selectedIndex = navItems.findIndex((item) => item.id === currentId);

      if (e.key === "j" && selectedIndex < navItems.length - 1) {
        const nextItem = navItems[selectedIndex + 1];
        if (nextItem) {
          setState((s) => ({
            ...s,
            selectedYear: parseSelectedId(nextItem.id),
          }));
        }
      }
      if (e.key === "k" && selectedIndex > 0) {
        const prevItem = navItems[selectedIndex - 1];
        if (prevItem) {
          setState((s) => ({
            ...s,
            selectedYear: parseSelectedId(prevItem.id),
          }));
        }
      }
    },
    [state.selectedYear, navItems],
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown]);

  async function processUpload(file: File, apiKey: string): Promise<TaxReturn> {
    const formData = new FormData();
    formData.append("pdf", file);
    if (apiKey) formData.append("apiKey", apiKey);

    const res = await fetch("/api/parse", { method: "POST", body: formData });
    if (!res.ok) {
      const { error } = await res.json();
      throw new Error(error || `HTTP ${res.status}`);
    }

    const taxReturn: TaxReturn = await res.json();
    const returnsRes = await fetch("/api/returns");
    const returns = await returnsRes.json();

    setState((s) => ({
      ...s,
      returns,
      providerKeys: {
        ...s.providerKeys,
        [getProviderForFamily(s.modelConfig.parseFamily)]: true,
      },
      hasUserData: true,
      // Stay on summary if already there, otherwise navigate to new year
      selectedYear: s.selectedYear === "summary" ? "summary" : taxReturn.year,
    }));

    return taxReturn;
  }

  async function handleUploadFromSidebar(files: File[]) {
    if (files.length === 0) return;

    // If no API key, open modal with all files
    if (!hasParseProviderKey) {
      setPendingFiles(files);
      setIsModalOpen(true);
      return;
    }

    // Create pending uploads immediately (optimistic) for all files
    const newPendingUploads: PendingUpload[] = files.map((file) => {
      const filenameYear = extractYearFromFilename(file.name);
      return {
        id: crypto.randomUUID(),
        filename: file.name,
        year: filenameYear,
        status: filenameYear ? "parsing" : "extracting-year",
        file,
      };
    });

    setPendingUploads((prev) => [...prev, ...newPendingUploads]);

    // Select the first pending upload
    const firstPending = newPendingUploads[0];
    if (firstPending) {
      setState((s) => ({ ...s, selectedYear: `pending:${firstPending.id}` }));
    }

    // Extract years in parallel for files that don't have one from filename
    await Promise.all(
      newPendingUploads
        .filter((p) => !p.year)
        .map(async (pending) => {
          try {
            const formData = new FormData();
            formData.append("pdf", pending.file);
            const yearRes = await fetch("/api/extract-year", {
              method: "POST",
              body: formData,
            });
            const { year: extractedYear } = await yearRes.json();
            setPendingUploads((prev) =>
              prev.map((p) =>
                p.id === pending.id
                  ? { ...p, year: extractedYear, status: "parsing" }
                  : p,
              ),
            );
          } catch (err) {
            console.error("Year extraction failed:", err);
            setPendingUploads((prev) =>
              prev.map((p) =>
                p.id === pending.id ? { ...p, status: "parsing" } : p,
              ),
            );
          }
        }),
    );

    // Process files with concurrency limit of 2
    setIsUploading(true);
    let successfulUploads = 0;
    await withConcurrency(newPendingUploads, 2, async (pending) => {
      try {
        await processUpload(pending.file, "");
        successfulUploads++;
        setPendingUploads((prev) => prev.filter((p) => p.id !== pending.id));
      } catch (err) {
        console.error("Upload failed:", err);
        setPendingUploads((prev) => prev.filter((p) => p.id !== pending.id));
      }
    });
    setIsUploading(false);

    // Navigate to appropriate view after all uploads complete
    setState((s) => ({
      ...s,
      selectedYear: getDefaultSelection(s.returns),
    }));

    // Auto-trigger chat after successful upload
    if (successfulUploads > 0) {
      const autoMessage =
        files.length === 1
          ? "Help me understand my year"
          : "Help me understand my history of income and taxes";
      setPendingAutoMessage(autoMessage);
      setIsChatOpen(true);
    }
  }

  async function handleUploadFromModal(files: File[], apiKey: string) {
    for (const file of files) {
      await processUpload(file, apiKey);
    }
    setPendingFiles([]);
  }

  async function handleSaveProviderKey(provider: ProviderId, apiKey: string) {
    const res = await fetch("/api/config/keys", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ provider, apiKey }),
    });
    if (!res.ok) {
      const { error } = await res.json();
      throw new Error(error || `HTTP ${res.status}`);
    }
    setState((s) => ({
      ...s,
      providerKeys: {
        ...s.providerKeys,
        [provider]: true,
      },
    }));
  }

  async function handleSaveModelConfig(config: ModelConfig) {
    const res = await fetch("/api/config/models", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(config),
    });
    if (!res.ok) {
      const { error } = await res.json();
      throw new Error(error || `HTTP ${res.status}`);
    }
    setState((s) => ({ ...s, modelConfig: config }));
  }

  async function handleClearData() {
    const res = await fetch("/api/clear-data", { method: "POST" });
    if (!res.ok) {
      const { error } = await res.json();
      throw new Error(error || `HTTP ${res.status}`);
    }
    // Clear chat data from localStorage
    localStorage.removeItem(CHAT_OPEN_KEY);
    localStorage.removeItem(CHAT_HISTORY_KEY);
    localStorage.removeItem("tax-chat-width");
    // Reload to get fresh state
    window.location.reload();
  }

  async function submitChatMessage(prompt: string) {
    if (!prompt || isChatLoading) return;

    // Clear follow-up suggestions when sending a new message
    setFollowUpSuggestions([]);

    const userMessage: ChatMessage = {
      id: crypto.randomUUID(),
      role: "user",
      content: prompt,
    };

    const assistantId = crypto.randomUUID();
    const assistantMessage: ChatMessage = {
      id: assistantId,
      role: "assistant",
      content: "",
    };

    setChatMessages((prev) => [...prev, userMessage, assistantMessage]);
    setIsChatLoading(true);

    try {
      const res = await fetch("/api/chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt,
          history: chatMessages,
        }),
      });

      if (!res.ok) {
        const { error } = await res.json();
        throw new Error(error || `HTTP ${res.status}`);
      }

      const reader = res.body?.getReader();
      if (!reader) throw new Error("No response body");

      const decoder = new TextDecoder();
      let accumulated = "";
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);

          if (payload === "[DONE]") continue;

          try {
            const parsed = JSON.parse(payload);
            if (parsed.error) throw new Error(parsed.error);
            if (parsed.text) {
              accumulated += parsed.text;
              setChatMessages((prev) =>
                prev.map((m) =>
                  m.id === assistantId ? { ...m, content: accumulated } : m,
                ),
              );
            }
          } catch (e) {
            if (e instanceof SyntaxError) continue;
            throw e;
          }
        }
      }

      // Fetch follow-up suggestions (non-blocking)
      const finalAssistant: ChatMessage = { ...assistantMessage, content: accumulated };
      setIsLoadingSuggestions(true);
      fetch("/api/suggestions", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          history: [...chatMessages, userMessage, finalAssistant],
        }),
      })
        .then((res) => res.json())
        .then(({ suggestions }) => setFollowUpSuggestions(suggestions || []))
        .catch(() => setFollowUpSuggestions([]))
        .finally(() => setIsLoadingSuggestions(false));
    } catch (err) {
      console.error("Chat error:", err);
      setChatMessages((prev) =>
        prev.map((m) =>
          m.id === assistantId
            ? { ...m, content: `Error: ${err instanceof Error ? err.message : "Failed to get response"}` }
            : m,
        ),
      );
    } finally {
      setIsChatLoading(false);
    }
  }

  function handleNewChat() {
    setChatMessages([]);
    saveChatMessages([]);
    setFollowUpSuggestions([]);
  }

  function handleSelect(id: string) {
    setState((s) => ({
      ...s,
      selectedYear: parseSelectedId(id),
    }));
  }

  async function handleDelete(id: string) {
    const year = Number(id);
    if (isNaN(year)) return;

    await fetch(`/api/returns/${year}`, { method: "DELETE" });

    // Check if this is the last year before updating state
    const isLastYear = Object.keys(state.returns).length === 1;

    setState((s) => {
      const newReturns = { ...s.returns };
      delete newReturns[year];

      if (isLastYear) {
        // Last year deleted - reset to true empty state
        return {
          ...s,
          returns: {},
          selectedYear: "summary",
          hasUserData: false,
        };
      }

      const newSelection =
        s.selectedYear === year
          ? getDefaultSelection(newReturns)
          : s.selectedYear;
      return {
        ...s,
        returns: newReturns,
        selectedYear: newSelection,
      };
    });

    // Re-open onboarding if we just deleted the last year
    if (isLastYear) {
      setOpenModal("onboarding");
    }
  }

  if (state.isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <span className="text-sm text-(--color-text-muted)">Loading...</span>
      </div>
    );
  }

  function getSelectedId(): string {
    if (
      typeof state.selectedYear === "string" &&
      state.selectedYear.startsWith("pending:")
    ) {
      return state.selectedYear;
    }
    if (state.selectedYear === "summary") return "summary";
    return String(state.selectedYear);
  }
  const selectedId = getSelectedId();

  function getReceiptData(): TaxReturn | null {
    if (typeof state.selectedYear === "number") {
      return effectiveReturns[state.selectedYear] || null;
    }
    return null;
  }

  function renderMainPanel() {
    // Calculate selectedYear for StatsHeader (always "summary" or a number)
    const statsSelectedYear: "summary" | number =
      typeof state.selectedYear === "number" ? state.selectedYear : "summary";

    const commonProps = {
      isChatOpen,
      isChatLoading,
      onToggleChat: () => setIsChatOpen(!isChatOpen),
      showChatButton: true,
      navItems,
      selectedId,
      onSelect: handleSelect,
      onOpenStart: () => setOpenModal("onboarding"),
      onOpenReset: () => setOpenModal("reset"),
      onOpenHelp: () => setOpenModal("help"),
      onDeleteYear: handleDelete,
      hasUserData: state.hasUserData,
      hasStoredKey: hasAnyProviderKey,
      returns: effectiveReturns,
      selectedYear: statsSelectedYear,
    };

    if (selectedPendingUpload) {
      return (
        <MainPanel
          view="loading"
          pendingUpload={selectedPendingUpload}
          {...commonProps}
        />
      );
    }
    if (state.selectedYear === "summary") {
      return <MainPanel view="summary" {...commonProps} />;
    }
    const receiptData = getReceiptData();
    if (receiptData) {
      return (
        <MainPanel
          view="receipt"
          data={receiptData}
          title={String(state.selectedYear)}
          {...commonProps}
        />
      );
    }
    return <MainPanel view="summary" {...commonProps} />;
  }

  // Find pending upload if selected
  const selectedPendingUpload =
    typeof state.selectedYear === "string" &&
    state.selectedYear.startsWith("pending:")
      ? pendingUploads.find((p) => `pending:${p.id}` === state.selectedYear)
      : null;

  // Show onboarding dialog for new users (unless dismissed) or when manually opened
  // Processing takes precedence - keep dialog open while processing
  const showOnboarding =
    isOnboardingProcessing ||
    openModal === "onboarding" ||
    (!onboardingDismissed &&
      !hasParseProviderKey &&
      !state.hasUserData);

  // Skip open animation only on first automatic show (not manual reopen)
  const skipOnboardingAnimation =
    showOnboarding &&
    !hasShownOnboardingRef.current &&
    openModal !== "onboarding";
  if (showOnboarding && !hasShownOnboardingRef.current) {
    hasShownOnboardingRef.current = true;
  }

  function getPostUploadNavigation(
    existingYears: number[],
    uploadedYears: number[],
    batchSize: number,
  ): SelectedView {
    if (uploadedYears.length === 0) return "summary"; // all failed
    if (batchSize === 1) return uploadedYears[0]!; // single file -> that year
    return "summary"; // multiple files -> summary
  }

  async function handleOnboardingUpload(files: FileWithId[], apiKey: string) {
    setIsOnboardingProcessing(true);
    const existingYears = Object.keys(state.returns).map(Number);

    // Initialize progress using the same IDs from SetupDialog
    const progress: FileProgress[] = files.map((f) => ({
      id: f.id,
      filename: f.file.name,
      status: "pending" as const,
    }));
    setOnboardingProgress(progress);

    // Save parse-provider key if needed
    if (!hasParseProviderKey && apiKey) {
      await handleSaveProviderKey(parseProvider, apiKey);
    }

    // Process files with concurrency limit of 2
    const uploadedYears: number[] = [];
    await withConcurrency(files, 2, async (fileWithId) => {
      const { file, id } = fileWithId;

      setOnboardingProgress((p) =>
        p.map((f) => (f.id === id ? { ...f, status: "parsing" } : f)),
      );

      try {
        const taxReturn = await processUpload(file, apiKey);
        uploadedYears.push(taxReturn.year);
        setOnboardingProgress((p) =>
          p.map((f) =>
            f.id === id
              ? { ...f, status: "complete", year: taxReturn.year }
              : f,
          ),
        );
      } catch (err) {
        setOnboardingProgress((p) =>
          p.map((f) =>
            f.id === id
              ? {
                  ...f,
                  status: "error",
                  error: err instanceof Error ? err.message : "Failed",
                }
              : f,
          ),
        );
      }
    });

    // Smart routing
    const nav = getPostUploadNavigation(
      existingYears,
      uploadedYears,
      files.length,
    );
    setState((s) => ({ ...s, selectedYear: nav }));

    setIsOnboardingProcessing(false);
    setOpenModal(null);
    setOnboardingProgress([]);

    // Auto-trigger chat after successful upload
    if (uploadedYears.length > 0) {
      const autoMessage =
        files.length === 1
          ? "Help me understand my year"
          : "Help me understand my history of income and taxes";
      setPendingAutoMessage(autoMessage);
      setIsChatOpen(true);
    }
  }

  function handleOnboardingClose() {
    setOpenModal(null);
    setOnboardingDismissed(true);
  }

  // Dev helper: throws during render to test ErrorBoundary
  if (devTriggerError) {
    throw new Error("Test error triggered from dev tools");
  }

  return (
    <div className="flex h-screen">
      <ErrorBoundary name="Main Panel">{renderMainPanel()}</ErrorBoundary>

      {isChatOpen && (
        <ErrorBoundary name="Chat">
          <Chat
            messages={chatMessages}
            isLoading={isChatLoading}
            hasApiKey={hasAnswerProviderKey}
            onSubmit={submitChatMessage}
            onNewChat={handleNewChat}
            onClose={() => setIsChatOpen(false)}
            followUpSuggestions={followUpSuggestions}
            isLoadingSuggestions={isLoadingSuggestions}
          />
        </ErrorBoundary>
      )}

      <SetupDialog
        isOpen={showOnboarding}
        onUpload={handleOnboardingUpload}
        onClose={handleOnboardingClose}
        isProcessing={isOnboardingProcessing}
        fileProgress={onboardingProgress}
        hasStoredKey={hasParseProviderKey}
        providerLabel={parseProviderLabel}
        provider={parseProvider}
        existingYears={Object.keys(state.returns).map(Number)}
        skipOpenAnimation={skipOnboardingAnimation}
      />

      <UploadModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setPendingFiles([]);
          setConfigureKeyOnly(false);
        }}
        onUpload={handleUploadFromModal}
        onSaveApiKey={(key) => handleSaveProviderKey(parseProvider, key)}
        hasStoredKey={hasParseProviderKey}
        providerLabel={parseProviderLabel}
        provider={parseProvider}
        pendingFiles={pendingFiles}
        configureKeyOnly={configureKeyOnly}
      />

      <SettingsModal
        isOpen={openModal === "settings"}
        onClose={() => setOpenModal(null)}
        providerKeys={state.providerKeys}
        onSaveProviderKey={handleSaveProviderKey}
        onClearData={handleClearData}
        modelConfig={state.modelConfig}
        onSaveModelConfig={handleSaveModelConfig}
      />

      <ResetDialog
        isOpen={openModal === "reset"}
        onClose={() => setOpenModal(null)}
        onReset={handleClearData}
      />

      <HelpDialog
        isOpen={openModal === "help"}
        onClose={() => setOpenModal(null)}
      />

      {state.isDev && (
        <DevTools onTriggerError={() => setDevTriggerError(true)} />
      )}
    </div>
  );
}
